import { usePersistedState } from "@/lib/persistedFilters";
// UC CDR → Rates. Load a WebCDR rate export, browse what it priced, and ask
// "what does this number cost on this plan?" without leaving the page.
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Calculator, Loader2, Search, Trash2, Upload } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Pill, clock } from "./ui";

export default function Rates() {
  const fileRef = useRef(null);
  const [imports, setImports] = useState([]);
  const [busy, setBusy] = useState("");
  const [filters, setFilters] = usePersistedState("uccdr-rates.filters", { q: "", plan: "", currency: "", page: 1 });
  const [data, setData] = useState(null);
  const [probe, setProbe] = useState({ number: "", plan: "Standard", seconds: 60 });
  const [result, setResult] = useState(null);

  const loadImports = useCallback(async () => {
    const { data: d } = await api.get("/uc-cdr/rates/imports");
    setImports(d.imports || []);
  }, []);

  const loadRates = useCallback(async () => {
    const { data: d } = await api.get("/uc-cdr/rates", { params: { ...filters, page_size: 25 } });
    setData(d);
  }, [filters]);

  useEffect(() => { loadImports(); }, [loadImports]);
  useEffect(() => { loadRates(); }, [loadRates]);

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return toast.error("Pick a rate export first");
    setBusy("upload");
    const form = new FormData();
    form.append("file", file);
    try {
      const { data: d } = await api.post("/uc-cdr/rates/import", form,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 300000 });
      toast.success(`${d.rate_rows.toLocaleString()} prefixes loaded from ${d.sheet_rows.toLocaleString()} sheet rows`);
      fileRef.current.value = "";
      await Promise.all([loadImports(), loadRates()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not read that rate sheet");
    } finally {
      setBusy("");
    }
  };

  const removeImport = async (importId) => {
    if (!window.confirm("Remove every rate row from this import?")) return;
    setBusy(importId);
    try {
      const { data: d } = await api.delete(`/uc-cdr/rates/imports/${importId}`);
      toast.success(`${d.removed.toLocaleString()} rate rows removed`);
      await Promise.all([loadImports(), loadRates()]);
    } finally {
      setBusy("");
    }
  };

  const lookup = async () => {
    if (!probe.number.trim()) return toast.error("Enter a number");
    setBusy("lookup");
    try {
      const { data: d } = await api.get("/uc-cdr/rates/lookup", { params: probe });
      setResult(d);
    } finally {
      setBusy("");
    }
  };

  const plans = data?.plans || [];
  const pages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

  return (
    <div className="space-y-4" data-testid="uc-cdr-rates">
      <Card className="px-4 py-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="grow">
            <Label className="text-xs text-slate-500">WebCDR rate export (.xlsx or .csv)</Label>
            <input ref={fileRef} type="file" accept=".xlsx,.xlsm,.csv" data-testid="uc-cdr-rate-file"
                   className="mt-1 block w-full cursor-pointer rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600 file:mr-3 file:rounded file:border-0 file:bg-slate-100 file:px-3 file:py-1.5 file:text-sm" />
          </div>
          <Button size="sm" onClick={upload} disabled={busy === "upload"} data-testid="uc-cdr-rate-import">
            {busy === "upload" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Import
          </Button>
        </div>
        <p className="mt-2 text-[12px] text-slate-500">
          A sheet is a full snapshot: importing retires the rows it replaces for the same plan
          and currency. Prefix lists expand to one row each, <code>{"{NANPA TOLL-FREE}"}</code>{" "}
          expands to the 8xx set, and the currency is read off each rate — CAD and USD sheets
          live side by side and are never converted.
        </p>
      </Card>

      <Card className="px-4 py-4">
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <Label className="text-xs text-slate-500">Number</Label>
            <Input className="w-48" placeholder="14032899142" value={probe.number}
                   data-testid="uc-cdr-lookup-number"
                   onChange={(e) => setProbe((p) => ({ ...p, number: e.target.value }))} />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Plan</Label>
            <select value={probe.plan} data-testid="uc-cdr-lookup-plan"
                    onChange={(e) => setProbe((p) => ({ ...p, plan: e.target.value }))}
                    className="h-10 w-48 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700">
              {(plans.length ? plans : ["Standard"]).map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Seconds</Label>
            <Input className="w-24" type="number" value={probe.seconds}
                   data-testid="uc-cdr-lookup-seconds"
                   onChange={(e) => setProbe((p) => ({ ...p, seconds: e.target.value }))} />
          </div>
          <Button size="sm" variant="outline" onClick={lookup} disabled={busy === "lookup"}
                  data-testid="uc-cdr-lookup-go">
            <Calculator className="h-4 w-4" /> What does it cost?
          </Button>
          {result && (
            <div className="ml-2 text-sm" data-testid="uc-cdr-lookup-result">
              {result.ok ? (
                <span className="text-slate-700">
                  <strong>{result.region}</strong> · prefix {result.prefix} · {result.rate}{" "}
                  {result.currency}/min · {result.rounding} → bills {result.billed_seconds}s ={" "}
                  <strong>{result.amount?.toFixed(4)} {result.currency}</strong>
                </span>
              ) : <span className="text-amber-700">{result.error}</span>}
            </div>
          )}
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-2.5">
          <span className="text-sm font-semibold text-slate-700">Rate rows</span>
          <div className="relative ml-2">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <Input className="w-56 pl-8" placeholder="Prefix or region" value={filters.q}
                   data-testid="uc-cdr-rate-search"
                   onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value, page: 1 }))} />
          </div>
          <select value={filters.plan} data-testid="uc-cdr-rate-plan-filter"
                  onChange={(e) => setFilters((f) => ({ ...f, plan: e.target.value, page: 1 }))}
                  className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700">
            <option value="">All plans</option>
            {plans.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <span className="ml-auto text-xs text-slate-500" data-testid="uc-cdr-rate-total">
            {(data?.total || 0).toLocaleString()} rows
          </span>
        </div>
        <table className="w-full text-left text-sm">
          <thead className="text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-1.5">Prefix</th>
              <th className="px-4 py-1.5">Region</th>
              <th className="px-4 py-1.5">Plan</th>
              <th className="px-4 py-1.5">Dir</th>
              <th className="px-4 py-1.5">Rate / min</th>
              <th className="px-4 py-1.5">Rounding</th>
              <th className="px-4 py-1.5">From</th>
            </tr>
          </thead>
          <tbody data-testid="uc-cdr-rates-body">
            {(data?.rates || []).map((r) => (
              <tr key={r.rate_id} className="border-t border-slate-100">
                <td className="px-4 py-1.5 font-mono text-[12px] text-slate-800">{r.prefix}</td>
                <td className="px-4 py-1.5 text-slate-700">{r.region}</td>
                <td className="px-4 py-1.5 text-slate-600">{r.plan}</td>
                <td className="px-4 py-1.5"><Pill>{r.direction}</Pill></td>
                <td className="px-4 py-1.5 text-slate-800">{r.rate} {r.currency}</td>
                <td className="px-4 py-1.5 text-slate-500">{r.minimum_seconds}/{r.increment_seconds}</td>
                <td className="px-4 py-1.5 text-[12px] text-slate-500">{r.effective_from || "—"}</td>
              </tr>
            ))}
            {!data?.rates?.length && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-slate-500"
                      data-testid="uc-cdr-rates-empty">
                No rate rows yet — import a WebCDR rate export above.
              </td></tr>
            )}
          </tbody>
        </table>
        {pages > 1 && (
          <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-4 py-2 text-sm">
            <Button size="sm" variant="outline" disabled={filters.page <= 1} data-testid="uc-cdr-rate-prev"
                    onClick={() => setFilters((f) => ({ ...f, page: f.page - 1 }))}>Previous</Button>
            <span className="text-xs text-slate-500">Page {filters.page} of {pages}</span>
            <Button size="sm" variant="outline" disabled={filters.page >= pages} data-testid="uc-cdr-rate-next"
                    onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))}>Next</Button>
          </div>
        )}
      </Card>

      <Card className="overflow-hidden">
        <div className="border-b border-slate-100 px-4 py-2.5 text-sm font-semibold text-slate-700">
          Imports
        </div>
        {!imports.length ? (
          <div className="px-4 py-6 text-sm text-slate-500" data-testid="uc-cdr-imports-empty">
            Nothing imported yet.
          </div>
        ) : (
          <table className="w-full text-left text-sm" data-testid="uc-cdr-imports">
            <tbody>
              {imports.map((im) => (
                <tr key={im.import_id} className="border-t border-slate-100">
                  <td className="px-4 py-1.5 font-mono text-[12px] text-slate-600">{clock(im.imported_at)}</td>
                  <td className="px-4 py-1.5 text-slate-700">{im.filename}</td>
                  <td className="px-4 py-1.5 text-slate-600">
                    {im.rate_rows?.toLocaleString()} prefixes · {im.sheet_rows?.toLocaleString()} sheet rows
                  </td>
                  <td className="px-4 py-1.5 text-[12px] text-slate-500">
                    {(im.plans || []).join(", ")} · {(im.currencies || []).join(", ")}
                  </td>
                  <td className="px-4 py-1.5 text-[12px] text-slate-500">{im.actor}</td>
                  <td className="px-4 py-1.5 text-right">
                    <Button size="sm" variant="ghost" disabled={busy === im.import_id}
                            onClick={() => removeImport(im.import_id)}
                            data-testid={`uc-cdr-import-delete-${im.import_id}`}>
                      <Trash2 className="h-4 w-4 text-rose-600" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
